// What a portal screen is allowed to be coloured.
//
// The same reasoning as invoiceStatus: the DECISION is shared, the RENDERING is
// not. Four hand-rolled copies of the status ternary drifted into showing one
// overdue invoice in two colours; a colour vocabulary written twice — once in
// Tailwind for web, once in NativeWind for native — drifts the same way, only
// across every screen at once instead of one badge.
//
// So: semantic roles resolved here as plain strings, applied by each platform's
// adapter. No DOM, no NativeWind, no `window`. See docs/customer-portal.md §5.

/** Light or dark. `system` is a *preference*; it resolves to one of these. */
export type ColorScheme = 'light' | 'dark';

/** What the customer chose. Persisted; `system` follows the device. */
export type ColorSchemePreference = ColorScheme | 'system';

/**
 * Semantic roles, never literal colours.
 *
 * A screen says "this is a surface" and "this is muted text", not "this is
 * white" and "this is gray-400" — because the second sentence is false in dark
 * mode, and a screen has no business knowing which mode it is in.
 */
export interface ThemeTokens {
  /** Page background, behind everything. */
  canvas: string;
  /** Card and panel background. */
  surface: string;
  /** Subtle fill inside a surface — table headers, empty states. */
  surfaceMuted: string;
  /** A step above surfaceMuted: chips, skeletons, dividers with weight. */
  surfaceRaised: string;
  /** Scrim behind a modal. Applied at partial opacity, e.g. `bg-overlay/50`. */
  overlay: string;

  /** Primary body and heading text. */
  content: string;
  /** Secondary text — labels, sub-headings. */
  contentSecondary: string;
  /** Muted text — captions, metadata, placeholder. */
  contentMuted: string;
  /** Faint text and disabled icons. Decorative only, never load-bearing. */
  contentFaint: string;
  /** Text that sits on a filled dark/brand surface. */
  inverse: string;

  /** Default border. */
  border: string;
  /** Hairline border — list separators. */
  borderSubtle: string;
  /** Heavier border for form controls, which need to read as editable. */
  borderStrong: string;

  /** Tenant brand colour, contrast-corrected for the scheme. */
  brand: string;
  /** Brand, one step stronger, for hover/press. */
  brandHover: string;
  /** Tinted brand background — the indigo-50 role. */
  brandSubtle: string;
  /** Border on a brand-tinted surface — the indigo-100 role. */
  brandBorder: string;
  /** Text/icon placed ON `brand`. Guaranteed readable. */
  brandContrast: string;
  /** Tenant accent colour, contrast-corrected. */
  accent: string;

  // Each status has three roles, and the third is not decoration. `success` is
  // an icon or a standalone label on a normal surface; `successSubtle` is a
  // tinted background; `successStrong` is text placed ON that tint. Collapsing
  // the last two into one dropped badge contrast from 6.5:1 to 3.1:1 — below
  // AA — because a colour legible on white is not legible on its own tint.
  success: string;
  successSubtle: string;
  successStrong: string;
  danger: string;
  dangerSubtle: string;
  dangerStrong: string;
  warning: string;
  warningSubtle: string;
  warningStrong: string;
  info: string;
  infoSubtle: string;
  infoStrong: string;
}

export type ThemeTokenName = keyof ThemeTokens;

/** Branding as the tenant config returns it. Every field optional. */
export interface TenantBranding {
  primaryColor?: string | undefined;
  accentColor?: string | undefined;
  fontFamily?: string | undefined;
}

export interface ResolveThemeInput {
  scheme: ColorScheme;
  branding?: TenantBranding | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Colour maths. WCAG relative luminance and contrast ratio, so the contrast
// guard below is an actual measurement rather than a guess about what looks ok.
// ─────────────────────────────────────────────────────────────────────────────

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Parses #rgb / #rrggbb. Returns null for anything else — callers fall back. */
export function parseHex(value: string): Rgb | null {
  const match = HEX.exec(value.trim());
  if (!match) return null;

  let hex = match[1] as string;
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

export function toHex({ r, g, b }: Rgb): string {
  const pair = (n: number): string => clamp255(n).toString(16).padStart(2, '0');
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

/** WCAG 2.1 relative luminance. 0 = black, 1 = white. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1:1 (identical) to 21:1 (black on white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return ((light as number) + 0.05) / ((dark as number) + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

function mix(from: Rgb, to: Rgb, amount: number): Rgb {
  const t = Math.max(0, Math.min(1, amount));
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  };
}

/** Black or white, whichever is legible on `background`. */
export function readableOn(background: Rgb): string {
  return contrastRatio(background, WHITE) >= contrastRatio(background, BLACK)
    ? '#ffffff'
    : '#111827';
}

/**
 * Nudges `color` toward white or black until it clears `minRatio` against
 * `against`, giving up after 20 steps rather than looping.
 *
 * This is the whole point of resolving theme centrally. A tenant picks a brand
 * colour by looking at it on a white page; the same navy on a near-black
 * surface is unreadable. Correcting it per-screen would mean correcting it in
 * two codebases.
 */
export function ensureContrast(color: Rgb, against: Rgb, minRatio: number): Rgb {
  if (contrastRatio(color, against) >= minRatio) return color;

  // Move away from the background: lighten on dark, darken on light.
  const target = relativeLuminance(against) < 0.5 ? WHITE : BLACK;

  let best = color;
  for (let step = 1; step <= 20; step++) {
    const candidate = mix(color, target, step / 20);
    best = candidate;
    if (contrastRatio(candidate, against) >= minRatio) return candidate;
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
// Base palettes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Portalia's own colours — the palette before any tenant overrides it.
 *
 * The brand sheet (`/branding/ptalia-color.txt`) gives three values:
 *
 *   #EDECEB  warm off-white   → `canvas`
 *   #2477ED  blue             → `brand`
 *   #42D06E  green            → `accent`
 *
 * `brand` here is `#2373e5`, not `#2477ED`, and the difference is deliberate:
 * `brandContrast` text sits on `brand` in filled buttons (`ui/Button`) and
 * select options at normal size, which WCAG AA scores at 4.5:1. White on
 * #2477ED reaches only 4.25:1. Darkening the blue by 3.5% toward black lands
 * at 4.52:1 — visually indistinguishable, and it keeps every filled control
 * legible. #2477ED is still the identity colour and is used unmodified in the
 * app icon and anywhere it is not sitting behind text.
 */
export const DEFAULT_BRAND = '#2373e5';
export const DEFAULT_ACCENT = '#42d06e';

const LIGHT: ThemeTokens = {
  // Warm off-white from the brand sheet, not the old cool gray. Cards stay
  // pure white so they lift off it rather than blending in.
  canvas: '#edeceb',
  surface: '#ffffff',
  surfaceMuted: '#f6f5f4',
  surfaceRaised: '#eae8e6',
  overlay: '#111827',

  content: '#111827',
  contentSecondary: '#374151',
  // Darkened from #6b7280: against the warmer, darker canvas that scored
  // 4.10:1 — under AA for the captions and metadata this role carries.
  contentMuted: '#656b78',
  contentFaint: '#8b91a0',
  inverse: '#ffffff',

  border: '#dedbd8',
  borderSubtle: '#eae8e6',
  borderStrong: '#c8c4c0',

  brand: DEFAULT_BRAND,
  brandHover: '#1c5ebc',
  brandSubtle: '#edf4fd',
  brandBorder: '#d3e3fa',
  brandContrast: '#ffffff',
  accent: DEFAULT_ACCENT,

  success: '#059669',
  successSubtle: '#dcfce7',
  successStrong: '#166534',
  danger: '#dc2626',
  dangerSubtle: '#fee2e2',
  dangerStrong: '#991b1b',
  warning: '#d97706',
  warningSubtle: '#fef9c3',
  warningStrong: '#854d0e',
  info: '#2563eb',
  infoSubtle: '#dbeafe',
  infoStrong: '#1e40af',
};

/**
 * Not an inversion of LIGHT — a designed counterpart.
 *
 * Surfaces stay near-neutral rather than pure black (an OLED-black card with a
 * white border reads as a hole), and the status colours are lightened, because
 * the light-mode `danger` red on a dark surface fails contrast outright.
 */
const DARK: ThemeTokens = {
  canvas: '#0b0f19',
  surface: '#131826',
  surfaceMuted: '#1a2032',
  surfaceRaised: '#232a3d',
  overlay: '#000000',

  content: '#f3f4f6',
  contentSecondary: '#d1d5db',
  contentMuted: '#9ca3af',
  contentFaint: '#6b7280',
  inverse: '#0b0f19',

  border: '#2a3348',
  borderSubtle: '#1f2637',
  borderStrong: '#3d4759',

  // The brand blue lightened until it clears 4.5:1 on the dark surface; the
  // green already clears 8.8:1 unmodified.
  brand: '#317fee',
  brandHover: '#5696f1',
  brandSubtle: '#1a2f52',
  brandBorder: '#1e3f72',
  brandContrast: '#0b0f19',
  accent: '#42d06e',

  success: '#34d399',
  successSubtle: '#052e26',
  successStrong: '#6ee7b7',
  danger: '#f87171',
  dangerSubtle: '#3f1414',
  dangerStrong: '#fca5a5',
  warning: '#fbbf24',
  warningSubtle: '#3b2506',
  warningStrong: '#fcd34d',
  info: '#60a5fa',
  infoSubtle: '#10243f',
  infoStrong: '#93c5fd',
};

export const BASE_THEMES: Record<ColorScheme, ThemeTokens> = {
  light: LIGHT,
  dark: DARK,
};

// WCAG AA for large text / UI components. Body text on `content` already clears
// AA comfortably in both palettes; this guard is for the *tenant's* colour,
// which we do not control.
const MIN_BRAND_CONTRAST = 3.1;

/**
 * Overlays tenant branding onto the scheme's base palette.
 *
 * Tenant colours are corrected, not trusted: `brand` is pushed until it clears
 * contrast against that scheme's surface, and `brandContrast` is measured
 * against the corrected value so text on a brand-filled button is legible
 * whatever the tenant chose. An unparseable colour falls back to the base
 * palette rather than throwing — branding is decoration, and a bad hex in a
 * tenant record must not blank the portal.
 */
export function resolveTheme({ scheme, branding }: ResolveThemeInput): ThemeTokens {
  const base = BASE_THEMES[scheme];
  const surface = parseHex(base.surface) ?? WHITE;

  const tokens: ThemeTokens = { ...base };

  const primary = branding?.primaryColor ? parseHex(branding.primaryColor) : null;
  if (primary) {
    const corrected = ensureContrast(primary, surface, MIN_BRAND_CONTRAST);
    tokens.brand = toHex(corrected);
    tokens.brandHover = toHex(mix(corrected, scheme === 'dark' ? WHITE : BLACK, 0.18));
    tokens.brandSubtle = toHex(mix(surface, corrected, scheme === 'dark' ? 0.22 : 0.08));
    tokens.brandBorder = toHex(mix(surface, corrected, scheme === 'dark' ? 0.38 : 0.2));
    tokens.brandContrast = readableOn(corrected);
  }

  const accent = branding?.accentColor ? parseHex(branding.accentColor) : null;
  if (accent) {
    tokens.accent = toHex(ensureContrast(accent, surface, MIN_BRAND_CONTRAST));
  }

  return tokens;
}

/** Resolves a stored preference against the device's reported scheme. */
export function effectiveScheme(
  preference: ColorSchemePreference,
  systemScheme: ColorScheme
): ColorScheme {
  return preference === 'system' ? systemScheme : preference;
}

/** Storage key for the persisted preference. Shared so web and native agree. */
export const COLOR_SCHEME_STORAGE_KEY = 'portal.colorScheme';

export function isColorSchemePreference(value: unknown): value is ColorSchemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Token name → CSS custom property, e.g. `surfaceMuted` → `--surface-muted`.
 * Lives here, not in the web adapter, so the native adapter names its
 * NativeWind variables identically and the two cannot drift.
 */
export function cssVariableName(token: ThemeTokenName): string {
  return `--${token.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`)}`;
}

/**
 * `#4f46e5` → `"79 70 229"`.
 *
 * Channel triplets rather than hex, because that is what lets a utility class
 * apply opacity to a themed colour — `bg-surface/50` compiles to
 * `rgb(var(--surface) / 0.5)`, which cannot work if the variable already holds
 * a complete colour. Both Tailwind and NativeWind use this same trick, so the
 * two platforms stay on one representation.
 */
export function toRgbChannels(color: string): string {
  const rgb = parseHex(color);
  if (!rgb) return '0 0 0';
  return `${clamp255(rgb.r)} ${clamp255(rgb.g)} ${clamp255(rgb.b)}`;
}

/** Every token as `{ '--surface': '255 255 255', … }`. Platform-agnostic. */
export function themeToCssVariables(tokens: ThemeTokens): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) {
    out[cssVariableName(name as ThemeTokenName)] = toRgbChannels(value);
  }
  return out;
}

/** Token names in declaration order — used to generate the stylesheet. */
export const THEME_TOKEN_NAMES = Object.keys(BASE_THEMES.light) as ThemeTokenName[];
